// Modules
import { useContext, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

// Context
import Data from 'context/Data'
import Dispatch from 'context/Dispatch'

// Reducers
import { SYSTEM_ACTIONS } from 'reducers/system'

// Hook: System > Language
function useLanguage (translationPath) {
	// Data
	const { direction, language } = useContext(Data)
	const dispatch = useContext(Dispatch)
	const { i18n, t } = useTranslation()

	// Data (Memoized)
	const label = useMemo(() => {
		// Instances
		const conjunction = new Intl.ListFormat(language, { style: 'long', type: 'conjunction' })
		const currencyNames = new Intl.DisplayNames([language], { type: 'currency' })
		// const dateTime = new Intl.DateTimeFormat(language)
		const dialects = new Intl.DisplayNames([language], { languageDisplay: 'dialect', type: 'language' })
		const disjunction = new Intl.ListFormat(language, { style: 'long', type: 'disjunction' })
		const languages = new Intl.DisplayNames([language], { type: 'language' })
		const numbers = new Intl.NumberFormat(language)
		const regions = new Intl.DisplayNames([language], { type: 'region' })
		const scripts = new Intl.DisplayNames([language], { type: 'script' })
		const shortNumbers = new Intl.NumberFormat(language, { notation: 'compact' })
		// Functions
		const getLanguageName = languages.of.bind(languages)
		// Response
		return {
			conjunction: list => conjunction.format(list),
			currency: currencyNames.of.bind(currencyNames),
			dialect: dialects.of.bind(dialects),
			disjunction: list => disjunction.format(list),
			language: (languageCode, fallbackLabel) => {
				const fallback = (fallbackLabel || languageCode)
				try {
					const calculatedOutput = getLanguageName(languageCode)
					const isConverted = !(
						calculatedOutput === languageCode || // Converted value cannot be the same
						calculatedOutput === calculatedOutput.toLowerCase() // Converted value cannot be lowercase
					)
					return isConverted ? calculatedOutput : (fallback || languageCode)
				} catch {
					return fallback || languageCode
				}
			},
			number: number => numbers.format(number),
			region: regions.of.bind(regions),
			script: scripts.of.bind(scripts),
			shortNumber: number => shortNumbers.format(number),
			time: (date, configuration) => new Intl.DateTimeFormat(language, configuration).format(date)
		}
	}, [language])
	const translate = useMemo(() => ({
		global: t,
		local: i18n.getFixedT(null, null, translationPath || '')
	}), [i18n, t, translationPath])

	// Functions (Memoized)
	const checkIfTranslatable = useCallback((scope, key, data = {}) => {
		return i18n.exists(scope === 'local' ? `${translationPath || ''}.${key}` : key, data)
	}, [i18n, translationPath])
	const setLanguage = useCallback(language => {
		dispatch({
			type: SYSTEM_ACTIONS.CHANGE_LANGUAGE,
			payload: language
		})
		window.dataLayer.push({ event: 'system-language-change' })
	}, [dispatch])

	// Response
	return {
		checkIfTranslatable,
		direction,
		label,
		language,
		setLanguage,
		translate
	}
}

// Export
export default useLanguage
